/** Shared fragment: API original image URL in an img `src` (optional ?query / #hash). */
const API_V1_IMAGE_ORIGINAL_IN_SRC =
  String.raw`\/api\/v1\/images\/[^\s"'<>]+\/original(?:\?[^\s"'<>]*)?(?:#[^\s"'<>]*)?`;

const API_V1_ORIGINAL_RE = new RegExp(API_V1_IMAGE_ORIGINAL_IN_SRC, "i");

/**
 * Match `src` on `<img>` with optional whitespace around `=`, quoted or unquoted.
 * Groups: 1=prefix through `src`, 2=double-quoted, 3=single-quoted, 4=unquoted.
 */
const IMG_SRC_ATTR_RE =
  /(<img\b[^>]*?\s)src\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi;

const BLOCK_TAG =
  /<\/?(?:p|div|h[1-6]|ul|ol|li|blockquote|pre|table|thead|tbody|tr|td|th|hr|section|article|header|footer|figure|figcaption|details|summary)\b/i;

function imgSrcFromMatch(m: RegExpExecArray): string {
  return (m[2] ?? m[3] ?? m[4] ?? "").trim();
}

export function isUndurableBlogImageSrc(src: string): boolean {
  const normalized = src.trim();
  if (/^(?:blob|data):/i.test(normalized)) return true;
  return API_V1_ORIGINAL_RE.test(normalized);
}

/**
 * True when `url` already points at our images CDN (skip re-import on paste).
 * Matches the production images host and the exact configured CDN hostname only
 * (no blanket `*.cloudfront.net` — attacker-controlled distributions must still import).
 */
export function isAlreadyHostedBlogImageUrl(url: string): boolean {
  try {
    const u = new URL(url.trim());
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    const host = u.hostname.toLowerCase();
    if (host === "images.maxoptionstradingcards.com") return true;
    const configured =
      typeof process !== "undefined"
        ? process.env["NEXT_PUBLIC_IMAGES_CDN_BASE_URL"] || process.env["IMAGES_CDN_BASE_URL"]
        : undefined;
    if (configured) {
      try {
        if (new URL(configured).hostname.toLowerCase() === host) return true;
      } catch {
        /* ignore invalid env */
      }
    }
    return false;
  } catch {
    return false;
  }
}

export function stripUndurableBlogImageSrcs(html: string): string {
  return html.replace(
    IMG_SRC_ATTR_RE,
    (full, prefix: string, dq?: string, sq?: string, uq?: string) => {
      const src = (dq ?? sq ?? uq ?? "").trim();
      if (src && isUndurableBlogImageSrc(src)) return prefix;
      return full;
    },
  );
}

/** True when HTML contains img srcs that must not be saved (blob, data, API /original). */
export function htmlHasUndurableBlogImages(html: string): boolean {
  IMG_SRC_ATTR_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = IMG_SRC_ATTR_RE.exec(html)) !== null) {
    const src = imgSrcFromMatch(m);
    if (src && isUndurableBlogImageSrc(src)) return true;
  }
  return false;
}

/**
 * Collect unique `https://` img srcs that are not already on our images CDN
 * (candidates for paste-time import).
 */
export function extractHttpsImageUrlsForImport(html: string): string[] {
  const urls: string[] = [];
  IMG_SRC_ATTR_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = IMG_SRC_ATTR_RE.exec(html)) !== null) {
    const src = imgSrcFromMatch(m);
    if (!/^https:\/\//i.test(src)) continue;
    if (isAlreadyHostedBlogImageUrl(src)) continue;
    urls.push(src);
  }
  return [...new Set(urls)];
}

function isUndurableTipTapImageSrc(src: string): boolean {
  return isUndurableBlogImageSrc(src);
}

/** Remove non-persistent `image` node `attrs.src` from TipTap JSON before DB persist. */
export function scrubUndurableBlogImageSrcsFromTipTapJson(
  root: Record<string, unknown>,
): Record<string, unknown> {
  function walk(node: unknown): unknown {
    if (!node || typeof node !== "object") return node;
    if (Array.isArray(node)) return node.map(walk);
    const obj = { ...(node as Record<string, unknown>) };
    if (obj.type === "image" && obj.attrs && typeof obj.attrs === "object") {
      const attrs = { ...(obj.attrs as Record<string, unknown>) };
      const src = attrs.src;
      if (typeof src === "string" && isUndurableTipTapImageSrc(src)) {
        delete attrs.src;
      }
      obj.attrs = attrs;
    }
    if (Array.isArray(obj.content)) {
      obj.content = obj.content.map(walk);
    }
    return obj;
  }
  return walk(root) as Record<string, unknown>;
}

/** Callers must sanitize HTML before invoking (e.g. DOMPurify). */
export function ensureBlogHtmlHasBlocks(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return trimmed;
  if (BLOCK_TAG.test(trimmed)) return html;
  return `<p>${trimmed}</p>`;
}
